import { Injectable } from '@angular/core';
import {
    ITenant,
    ITenantWebsiteConfig,
    ITenantDeleteResponse,
    ITenantDuplicatePayload,
    IOnboardedTenant,
    ITenantOnboardingPayload,
} from '../interfaces/tenant.interface';
import { IResponse } from '../../shared/interface/api.interface';
import { HttpClient, HttpParams } from '@angular/common/http';
import { buildHttpParamsForList, IPageProps } from '../../shared/utils/table';
import { IListOfValue } from '../../shared/interface/list-of-value.interface';
import { firstValueFrom } from 'rxjs';
import { ITenantStatsCounts } from '../../dashboard/interfaces/dashboard.interface';
import { IDraftWebConfigPayload } from '../../setting/interface/setting-theme-customizer.interface';
import { environment } from '../../../environments/environment.production';

@Injectable({
    providedIn: 'root',
})
export class TenantService {
    private apiUrl = '/tenants';
    private accessKey = environment.accessKey;

    constructor(private http: HttpClient) {}

    async getTenants(params?: IPageProps): Promise<IResponse<ITenant[]>> {
        const httpParams = buildHttpParamsForList(params);
        return firstValueFrom(
            this.http.get<IResponse<ITenant[]>>(this.apiUrl, { params: httpParams }),
        );
    }

    async getDemoTenants(params?: IPageProps): Promise<IResponse<ITenant[]>> {
        const httpParams = buildHttpParamsForList(params);
        return firstValueFrom(
            this.http.get<IResponse<ITenant[]>>(`${this.apiUrl}/demo`, { params: httpParams }),
        );
    }

    async getTenantById(id: string): Promise<ITenant> {
        return firstValueFrom(this.http.get<IResponse<ITenant>>(`${this.apiUrl}/${id}`)).then(
            (res) => res.data,
        );
    }

    async deleteTenant(id: string): Promise<ITenantDeleteResponse> {
        return firstValueFrom(
            this.http.delete<IResponse<ITenantDeleteResponse>>(`${this.apiUrl}/${id}`),
        ).then((res) => res.data);
    }

    async bulkDeleteTenants(ids: string[]): Promise<ITenantDeleteResponse> {
        return firstValueFrom(
            this.http.delete<IResponse<ITenantDeleteResponse>>(`${this.apiUrl}/bulk-delete`, {
                body: { ids },
            }),
        ).then((res) => res.data);
    }

    async restoreTenant(id: string): Promise<ITenantDeleteResponse> {
        return firstValueFrom(
            this.http.put<IResponse<ITenantDeleteResponse>>(`${this.apiUrl}/${id}/restore`, {}),
        ).then((res) => res.data);
    }

    async createTenant(tenant: ITenant): Promise<ITenant> {
        return firstValueFrom(this.http.post<IResponse<ITenant>>(this.apiUrl, tenant)).then(
            (res) => res.data,
        );
    }

    /*=====================================
      The merchant creating their OWN store at the end of the signup wizard.

      Not createTenant(): that one is the Super Admin building a store FOR
      somebody, and it answers on a different route behind different guards.
      This one creates a store for whoever holds the token, and returns a new
      token carrying its membership - so the merchant lands inside the store
      without signing in again.
      @param payload - what steps 1 and 2 collected
      @return the store and the token
    ======================================*/
    async onboardTenant(payload: ITenantOnboardingPayload): Promise<IOnboardedTenant> {
        return firstValueFrom(
            this.http.post<IResponse<IOnboardedTenant>>(`${this.apiUrl}/onboarding`, payload),
        ).then((res) => res.data);
    }

    async updateTenant(tenantId: string, tenant: ITenant): Promise<ITenant> {
        return firstValueFrom(
            this.http.patch<IResponse<ITenant>>(`${this.apiUrl}/${tenantId}`, tenant),
        ).then((res) => res.data);
    }

    async getTenantsLov(searchText = '', isDemoTenant?: boolean): Promise<IListOfValue[]> {
        let params = new HttpParams().set('searchText', searchText);
        if (isDemoTenant !== undefined) {
            params = params.set('isDemoTenant', String(isDemoTenant));
        }
        return firstValueFrom(
            this.http.get<IResponse<IListOfValue[]>>(`${this.apiUrl}/list-of-values`, { params }),
        ).then((res) => res.data);
    }

    async updateTenantAccount(id: string, tenant: ITenant): Promise<ITenant> {
        return firstValueFrom(
            this.http.patch<IResponse<ITenant>>(`${this.apiUrl}/${id}/profile`, tenant),
        ).then((res) => res.data);
    }

    async duplicateTenant(tenantId: string, payload: ITenantDuplicatePayload): Promise<ITenant> {
        return firstValueFrom(
            this.http.post<IResponse<ITenant>>(`${this.apiUrl}/${tenantId}/duplicate`, payload),
        ).then((res) => res.data);
    }

    async fetchTenantStatsCount(): Promise<ITenantStatsCounts> {
        return firstValueFrom(
            this.http.get<IResponse<ITenantStatsCounts>>(`${this.apiUrl}/count`),
        ).then((res) => res.data);
    }

    async getTenantConfig(host: string): Promise<ITenantWebsiteConfig> {
        return firstValueFrom(
            this.http.get<IResponse<ITenantWebsiteConfig>>('/web/tenants/config', {
                headers: {
                    'access-key': this.accessKey,
                    'tenant-host': host,
                },
            }),
        ).then((res) => res.data);
    }

    async saveDraftWebConfig(
        draftWebConfigPayload: IDraftWebConfigPayload,
    ): Promise<{ success: boolean }> {
        return firstValueFrom(
            this.http.patch<IResponse<{ success: boolean }>>(
                `${this.apiUrl}/draft-web-config`,
                draftWebConfigPayload,
            ),
        ).then((res) => res.data);
    }

    async resetDraftWebConfig(): Promise<{ success: boolean }> {
        return firstValueFrom(
            this.http.patch<IResponse<{ success: boolean }>>(
                `${this.apiUrl}/reset-draft-web-config`,
                {},
            ),
        ).then((res) => res.data);
    }
}
